import { Box, Grid, LinearProgress, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import { GridCloseIcon } from '@mui/x-data-grid';

import { useState, useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { cloneDeep } from 'lodash';
import { read, utils } from 'xlsx';

import { documentIcon, greenTick } from 'common/assets/images';
import ButtonComp from 'common/components/Button/ButtonComp';
import ModalEle from 'common/components/Modal/ModalEle';
import {
	AI_SIMULATION_BULK_UPLOAD_ERROR_TRY_AGAIN_MSG,
	AI_SIMULATION_BULK_UPLOAD_ERROR_MSG,
	COLUMN_HEADERS_DOES_NOT_EXIST,
	IColumnMappingData,
} from 'common/constants/AISimulationConstants';
import {
	DELETE_ALL_ATTACHMENTS_LABEL,
	DELETE_ALL_ATTACHMENT_LABEL,
	BUTTON_LABELS,
} from 'common/constants/index';
import { RootState } from 'common/store/AppStore';
import {
	setBulkUploadFailedStatus,
	setBulkUploadSuccessStatus,
	setControlQualityRetry,
} from 'common/store/reducer/commonReducer';
import { getFormattedSize, updateMap } from 'common/utils/utilFunctions';

import AIModalColumnMapping from './AIModalColumnMapping';

import styles from './BulkUploadModal.module.scss';

export interface IFinalMappedFileInfo {
	fileInfo: File;
	mappingInfo: Map<string, string>;
}
interface IModalProps {
	filesData: File[];
	modalHeading: string;
	openModal: boolean;
	handleClose: (type?: string) => void;
	handleSubmit: (mappings: any) => void;
	handleRetry: (mappings: any) => void;
	setUploadedFiles: React.Dispatch<React.SetStateAction<File[]>>;
	columnsData: IColumnMappingData[];
}
const sxBoxProps = {
	width: '37rem',
	height: '60rem',
	margin: 'auto',
	overflowX: 'unset',
	overflowY: 'unset',
	overflow: 'auto',
};
const { BACK, NEXT, UPLOAD_SUBMIT } = BUTTON_LABELS;
const BulkUploadModal = (props: IModalProps) => {
	const {
		modalHeading,
		filesData,
		openModal,
		handleClose,
		setUploadedFiles,
		handleSubmit,
		handleRetry,
		columnsData,
	} = props;
	const dispatch = useDispatch();
	const isBulkUploadSuccess = useSelector(
		(state: RootState) => state?.common?.isBulkUploadSuccess,
	);
	const isBulkUploadFailed = useSelector(
		(state: RootState) => state?.common?.isBulkUploadFailed,
	);
	const isUploadSelected = filesData?.length > 0;
	const uploadedFilesInfo = cloneDeep(filesData);
	const [handleNextSelected, setHandleNextSelected] = useState<boolean>(false);
	const [apiCalled, setApiCalled] = useState<boolean>(false);
	const [columnsList, setColumnsList] =
		useState<IColumnMappingData[]>(columnsData);
	const [mappingSelected, setMappingSelected] = useState<boolean>(false);
	const [apiLoading, setApiLoading] = useState<boolean>(false);
	const [finalMappedFileInfo, setFinalMappedFileInfo] =
		useState<IFinalMappedFileInfo[]>();
	const [validFile, setValidFile] = useState<boolean>(false);
	const handleNext = () => {
		if (!handleNextSelected) {
			setHandleNextSelected(true);
			dispatch(setControlQualityRetry(false));
		} else if (!isBulkUploadFailed) {
			setApiLoading(true);
			handleSubmit(finalMappedFileInfo);
			setApiCalled(true);
		}
	};
	const handleBack = () => {
		setApiLoading(false);
		setApiCalled(false);
		setHandleNextSelected(false);
		setMappingSelected(false);
		setFinalMappedFileInfo([]);
		dispatch(setControlQualityRetry(false));
		dispatch(setBulkUploadSuccessStatus(false));
		dispatch(setBulkUploadFailedStatus(false));
	};

	const onRetryAction = () => {
		handleRetry(finalMappedFileInfo);
	};

	const handleColumnSelection = (key: string, value: any, data: any) => {
		const tKey = key;
		setMappingSelected(true);

		setFinalMappedFileInfo((prevState: any) => {
			const prevData = prevState ? [...prevState] : [];
			const existingIndex = prevData.findIndex(
				(ele: any) => ele?.path === data?.path,
			);

			if (existingIndex >= 0) {
				const existingMapping = prevData[existingIndex].mappingInfo;
				const obj = updateMap(existingMapping, tKey, value);

				const updatedElement = {
					...prevData[existingIndex],
					mappingInfo: obj,
				};

				prevData[existingIndex] = updatedElement;
			} else {
				const temp = {
					fileInfo: data?.fileInfo,
					mappingInfo: new Map([[tKey, value]]),
				};
				prevData.push(temp);
			}
			return prevData;
		});
	};

	const checkColumnMapping = (data: any, file: File) => {
		setColumnsList((prevState: any) => {
			const prevData = [...prevState];
			prevData.forEach((ele: IColumnMappingData) => {
				ele.fileInfo = file;
				ele.value = Object.keys(data[0]);
			});
			return prevData;
		});
	};

	useEffect(() => {
		if (!apiCalled) {
			const processFiles = async () => {
				if (filesData?.length === 1) {
					const processFile = async (file: File) => {
						const ab = await file.arrayBuffer();
						const wb = read(ab);
						const ws = wb.Sheets[wb.SheetNames[0]];
						const data: any[] = utils.sheet_to_json<any>(ws, { defval: null });
						if (data?.length > 0) {
							checkColumnMapping(data, file);
							setValidFile(false);
						} else {
							setValidFile(true);
						}
					};
					await Promise.all(uploadedFilesInfo.map(processFile));
				}
			};
			resetFileProgress();
			processFiles();
		}
	}, [handleNextSelected]);

	const resetFileProgress = () => {
		filesData.forEach((file: any) => {
			file.progress = 0;
		});
	};
	useEffect(() => {
		if (apiCalled) {
			const interval = setInterval(() => {
				let allFilesCompleted = true;
				filesData.forEach((file: any) => {
					if (!isBulkUploadSuccess || !isBulkUploadFailed) {
						if (file.progress < 100) {
							file.progress += 10;
							allFilesCompleted = false;
						} else if (file.progress === 100) {
							file.progress = 100;
						}
					}
				});
				if (allFilesCompleted) {
					clearInterval(interval);
					setApiLoading(false);
				}
			}, 100);
			if (isBulkUploadSuccess || isBulkUploadFailed) {
				clearInterval(interval);
				setApiLoading(false);
				resetFileProgress();
			}
		}
	}, [apiCalled, isBulkUploadFailed, isBulkUploadSuccess]);

	return (
		<ModalEle
			open={openModal}
			sxBoxProps={sxBoxProps}
			dataTestId="bulk-upload-modal"
		>
			<Stack className={styles['bulk-upload-modal']}>
				<Stack direction="column">
					<Stack direction="row" justifyContent="space-between">
						<Typography className={styles['header-text']} data-testid="header">
							{modalHeading}
						</Typography>
						<GridCloseIcon
							onClick={() => handleClose('close')}
							className={styles['add-closemodal']}
							data-testid="close-icon"
						/>
					</Stack>
				</Stack>
				<Grid className={styles['range-date-picker-container']}>
					<Grid
						container
						mt={3}
						className={styles['range-date-picker-container-styles']}
					>
						{filesData && filesData?.length > 0 && (
							<>
								<Box
									key="filedropbox-container"
									className={
										handleNextSelected && !apiCalled
											? styles['filedropbox-filename-nextcontainer']
											: styles['filedropbox-filename-container']
									}
									data-testid="filedropbox-filename-container"
								>
									{filesData?.map((data: any) => (
										<>
											<Box
												key={data?.path}
												display="flex"
												justifyContent="space-between"
												alignItems="center"
												className={
													handleNextSelected && !apiCalled
														? styles['filedropbox-filename-maindiv-next']
														: styles['filedropbox-filename-maindiv']
												}
												pt={2}
											>
												<Stack
													direction="column"
													className={
														styles['filedropbox-filename-maindiv-text']
													}
												>
													<Box display="flex" alignItems="center">
														<img
															src={documentIcon}
															className={styles['document-icon']}
															alt="documentIcon"
														/>
														<Typography
															className={styles['filedropbox-filename']}
														>
															{data?.path}
														</Typography>
														{!isBulkUploadFailed && !validFile && (
															<Typography
																className={
																	!handleNextSelected
																		? styles['filedropbox-filesize']
																		: styles['filedropbox-filesize-next']
																}
															>
																{getFormattedSize(data?.size)}
															</Typography>
														)}
													</Box>
												</Stack>
												<Stack mr={handleNextSelected ? 2 : 0}>
													{!isBulkUploadFailed && !apiCalled && !validFile && (
														<div className={styles['filedrop-greentick']}>
															<img src={greenTick} alt="greentick" />
														</div>
													)}

													{apiCalled && !isBulkUploadFailed && (
														<div
															className={styles['filedropbox-progress-text']}
														>
															{data?.progress?.toFixed(0)}%
														</div>
													)}
												</Stack>
											</Box>

											{isUploadSelected && apiCalled && (
												<Stack
													direction="row"
													className={
														styles['filedropbox-progressbar-mainstack']
													}
												>
													<div
														data-testid="progress-bar"
														className={
															styles['filedropbox-progressbar-mainstack-inner']
														}
													>
														{apiCalled && (
															<LinearProgress
																variant="determinate"
																value={
																	!isBulkUploadFailed ? data?.progress : 50
																}
																className={
																	!isBulkUploadFailed
																		? styles['filedropbox-progressbar-primary']
																		: styles['filedropbox-progressbar-error']
																}
															/>
														)}
														{isBulkUploadFailed && (
															<Stack
																direction="row"
																className={styles['filedropbox-error-text']}
																justifyContent="center"
															>
																<Typography>
																	{AI_SIMULATION_BULK_UPLOAD_ERROR_MSG}&nbsp;
																</Typography>
																<Typography
																	onClick={onRetryAction}
																	className={
																		styles['filedropbox-error-text-underline']
																	}
																>
																	{
																		AI_SIMULATION_BULK_UPLOAD_ERROR_TRY_AGAIN_MSG
																	}
																</Typography>
															</Stack>
														)}
													</div>
												</Stack>
											)}
										</>
									))}
								</Box>
								<Box>
									{filesData.length > 0 &&
										isUploadSelected &&
										!handleNextSelected &&
										!validFile && (
											<Typography
												mt={1}
												mb={1}
												onClick={() => {
													setUploadedFiles([]);
												}}
												className={styles['filedropbox-delete-attachment']}
											>
												{filesData.length === 1
													? DELETE_ALL_ATTACHMENT_LABEL
													: DELETE_ALL_ATTACHMENTS_LABEL}
											</Typography>
										)}
								</Box>
								{handleNextSelected && !apiCalled && (
									<Box
										className={styles['filedropbox-columnmapping-maindiv']}
										data-testid="test-column"
									>
										<AIModalColumnMapping
											values={columnsList}
											handleSelection={handleColumnSelection}
										/>
									</Box>
								)}
							</>
						)}
					</Grid>
				</Grid>
				<Box className={styles['btn-box']} mt={1}>
					{validFile && (
						<Typography className={styles['valid-file-text']}>
							{COLUMN_HEADERS_DOES_NOT_EXIST}
						</Typography>
					)}
					<Stack
						direction="row"
						justifyContent="flex-end"
						className={styles['btn-grp']}
					>
						{handleNextSelected && (
							<ButtonComp
								btnType="secondary"
								clickHandler={handleBack}
								disabled={apiLoading}
								data-testid="back-btn"
							>
								{BACK}
							</ButtonComp>
						)}
						<ButtonComp
							btnType="primary"
							clickHandler={handleNext}
							disabled={
								handleNextSelected
									? filesData?.length === 0 ||
									  !mappingSelected ||
									  isBulkUploadFailed
									: validFile
							}
							data-testid="apply-btn"
							loading={apiLoading}
						>
							{!handleNextSelected ? NEXT : UPLOAD_SUBMIT}
						</ButtonComp>
					</Stack>
				</Box>
			</Stack>
		</ModalEle>
	);
};

export default BulkUploadModal;
